import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  MoreVert,
  Search,
  FilterList,
  LocalOffer,
  CheckCircle,
  Warning,
  Block,
  Star,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';

interface Offer {
  id: string;
  title: string;
  description: string;
  vendorId: string;
  vendorName: string;
  category: string;
  location: {
    address: string;
    city: string;
    state: string;
  };
  imageUrl?: string;
  validTill: string;
  status: 'active' | 'inactive' | 'paused' | 'expired';
  discountPercentage?: number;
  originalPrice?: number;
  offerPrice?: number;
  tags: string[];
  views: number;
  isFeatured: boolean;
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
}

const OfferManagement: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'pause' | 'activate' | 'feature'>('approve');
  const [isLoading, setIsLoading] = useState(true);

  const filterOffers = useCallback(() => {
    let filtered = offers;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(offer =>
        offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(offer => offer.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(offer => offer.category === categoryFilter);
    }

    setFilteredOffers(filtered);
  }, [offers, searchTerm, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    filterOffers();
  }, [filterOffers]);

  const fetchOffers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        // Transform backend data to frontend format
        const transformedOffers: Offer[] = data.data.offers.map((offer: any) => ({
          id: offer._id,
          title: offer.title,
          description: offer.description,
          vendorId: offer.vendorId,
          vendorName: offer.vendorName,
          category: offer.category?.name || 'Unknown',
          location: {
            address: offer.location?.address || '',
            city: offer.location?.city || '',
            state: offer.location?.state || '',
          },
          imageUrl: offer.imageUrl,
          validTill: offer.validTill,
          status: offer.status,
          discountPercentage: offer.discountPercentage,
          originalPrice: offer.originalPrice,
          offerPrice: offer.offerPrice,
          tags: offer.tags || [],
          views: offer.views || 0,
          isFeatured: offer.isFeatured,
          isApproved: offer.isApproved,
          approvedBy: offer.approvedBy,
          approvedAt: offer.approvedAt,
          createdAt: offer.createdAt,
        }));
        setOffers(transformedOffers);
      } else {
        // Fallback to mock data if API fails
        const mockOffers: Offer[] = [
        {
          id: '1',
          title: '50% Off Pizza',
          description: 'Get 50% off on all pizzas. Valid for dine-in and takeaway.',
          vendorId: 'vendor1',
          vendorName: 'Pizza Palace',
          category: 'Restaurant',
          location: {
            address: '123 Main St',
            city: 'New York',
            state: 'NY',
          },
          imageUrl: 'https://example.com/pizza.jpg',
          validTill: '2024-02-15T23:59:59Z',
          status: 'active',
          discountPercentage: 50,
          originalPrice: 20,
          offerPrice: 10,
          tags: ['pizza', 'food', 'discount'],
          views: 150,
          isFeatured: true,
          isApproved: true,
          approvedBy: 'admin1',
          approvedAt: '2024-01-10T10:00:00Z',
          createdAt: '2024-01-10T09:00:00Z',
        },
        {
          id: '2',
          title: 'Buy 2 Get 1 Free',
          description: 'Buy any 2 items and get 1 free. Limited time offer.',
          vendorId: 'vendor2',
          vendorName: 'Fashion Store',
          category: 'Retail',
          location: {
            address: '456 Oak Ave',
            city: 'Los Angeles',
            state: 'CA',
          },
          imageUrl: 'https://example.com/fashion.jpg',
          validTill: '2024-02-20T23:59:59Z',
          status: 'active',
          discountPercentage: 33,
          originalPrice: 60,
          offerPrice: 40,
          tags: ['fashion', 'clothing', 'sale'],
          views: 75,
          isFeatured: false,
          isApproved: false,
          createdAt: '2024-01-15T14:00:00Z',
        },
        {
          id: '3',
          title: 'Free Consultation',
          description: 'Get a free 30-minute consultation with our experts.',
          vendorId: 'vendor3',
          vendorName: 'Tech Solutions',
          category: 'Service',
          location: {
            address: '789 Pine St',
            city: 'Chicago',
            state: 'IL',
          },
          imageUrl: 'https://example.com/tech.jpg',
          validTill: '2024-02-10T23:59:59Z',
          status: 'active',
          discountPercentage: 100,
          originalPrice: 100,
          offerPrice: 0,
          tags: ['consultation', 'tech', 'free'],
          views: 200,
          isFeatured: false,
          isApproved: true,
          approvedBy: 'admin1',
          approvedAt: '2024-01-12T11:00:00Z',
          createdAt: '2024-01-12T10:00:00Z',
        },
      ];
      
      setOffers(mockOffers);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      setOffers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOfferAction = (offer: Offer, action: 'approve' | 'reject' | 'pause' | 'activate' | 'feature') => {
    setSelectedOffer(offer);
    setActionType(action);
    setConfirmDialog(true);
    setActionMenuAnchor(null);
  };

  const confirmAction = async () => {
    if (!selectedOffer) return;

    try {
      let endpoint = '';
      let method = 'PUT';

      const baseUrl = process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin';
      
      switch (actionType) {
        case 'approve':
          endpoint = `${baseUrl}/offers/${selectedOffer.id}/approve`;
          break;
        case 'reject':
          endpoint = `${baseUrl}/offers/${selectedOffer.id}/reject`;
          break;
        case 'pause':
          endpoint = `${baseUrl}/offers/${selectedOffer.id}/pause`;
          break;
        case 'activate':
          endpoint = `${baseUrl}/offers/${selectedOffer.id}/activate`;
          break;
        case 'feature':
          endpoint = `${baseUrl}/offers/${selectedOffer.id}/feature`;
          break;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(endpoint, { 
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        // Update local state
        setOffers(offers.map(offer =>
          offer.id === selectedOffer.id
            ? { 
                ...offer, 
                status: actionType === 'approve' ? 'active' : 
                       actionType === 'reject' ? 'inactive' :
                       actionType === 'pause' ? 'paused' :
                       actionType === 'activate' ? 'active' : offer.status,
                isApproved: actionType === 'approve' ? true : offer.isApproved,
                isFeatured: actionType === 'feature' ? true : offer.isFeatured
              }
            : offer
        ));
      }
    } catch (error) {
      console.error('Error performing action:', error);
    } finally {
      setConfirmDialog(false);
      setSelectedOffer(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'paused':
        return 'info';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle />;
      case 'pending':
        return <Warning />;
      case 'paused':
        return <Block />;
      case 'expired':
        return <Block />;
      default:
        return <Warning />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) {
      return 'N/A';
    }
    return `$${price.toFixed(2)}`;
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Offer',
      width: 300,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ mr: 2, width: 40, height: 40, bgcolor: 'primary.light' }}>
            <LocalOffer />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.vendorName}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 120,
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 150,
      renderCell: (params) => (
        <Typography variant="caption">
          {params.value.city}, {params.value.state}
        </Typography>
      ),
    },
    {
      field: 'discountPercentage',
      headerName: 'Discount',
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" color="success.main" fontWeight="bold">
          {params.value ? `${params.value}%` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'originalPrice',
      headerName: 'Price',
      width: 120,
      renderCell: (params) => (
        <Box>
          {params.row.originalPrice && (
            <Typography variant="caption" sx={{ textDecoration: 'line-through' }}>
              {formatPrice(params.row.originalPrice)}
            </Typography>
          )}
          {params.row.offerPrice !== undefined && (
            <Typography variant="body2" fontWeight="bold" color="success.main">
              {formatPrice(params.row.offerPrice)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'views',
      headerName: 'Views',
      width: 80,
      type: 'number',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'isFeatured',
      headerName: 'Featured',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'primary' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'validTill',
      headerName: 'Valid Until',
      width: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MoreVert />}
          label="More"
          onClick={(event) => {
            setActionMenuAnchor(event.currentTarget);
            setSelectedOffer(params.row);
          }}
        />,
      ],
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Offer Management
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage and monitor all offers on the platform.
      </Typography>

      {/* Filters */}
      <Card sx={{ 
        mb: 3, 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
              <TextField
                fullWidth
                placeholder="Search offers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Restaurant">Restaurant</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Entertainment">Entertainment</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                fullWidth
              >
                More Filters
              </Button>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small">
                  Export
                </Button>
                <Button variant="outlined" size="small">
                  Bulk Actions
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Offers Table */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredOffers}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              loading={isLoading}
              sx={{
                border: 'none',
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #e9ecef',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#495057',
                  height: '56px',
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-columnHeaderTitleContainer': {
                    padding: '0 16px',
                  },
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f1f3f4',
                  padding: '0 16px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#495057',
                  height: '72px',
                  minHeight: '72px !important',
                  '& .MuiBox-root': {
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '8px',
                  },
                },
                '& .MuiDataGrid-row': {
                  display: 'flex',
                  alignItems: 'stretch',
                  minHeight: '72px !important',
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafbfc',
                  },
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e3f2fd !important',
                  transform: 'scale(1.001)',
                  transition: 'all 0.2s ease-in-out',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e9ecef',
                  padding: '16px 20px',
                  backgroundColor: '#f8f9fa',
                  minHeight: '64px',
                },
                '& .MuiDataGrid-selectedRowCount': {
                  color: '#3B3B3B',
                  fontWeight: 500,
                },
                '& .MuiTablePagination-root': {
                  color: '#495057',
                },
                '& .MuiCheckbox-root': {
                  color: '#3B3B3B',
                  padding: '8px',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer': {
                  visibility: 'visible',
                },
                '& .MuiDataGrid-sortIcon': {
                  opacity: 0.6,
                },
                '& .MuiDataGrid-columnHeader': {
                  '&:focus': {
                    outline: 'none',
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => handleOfferAction(selectedOffer!, 'approve')}
          disabled={selectedOffer?.isApproved}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Approve
        </MenuItem>
        <MenuItem
          onClick={() => handleOfferAction(selectedOffer!, 'reject')}
          disabled={!selectedOffer?.isApproved}
        >
          <Block sx={{ mr: 1 }} />
          Reject
        </MenuItem>
        <MenuItem
          onClick={() => handleOfferAction(selectedOffer!, 'pause')}
          disabled={selectedOffer?.status !== 'active'}
        >
          <Warning sx={{ mr: 1 }} />
          Pause
        </MenuItem>
        <MenuItem
          onClick={() => handleOfferAction(selectedOffer!, 'activate')}
          disabled={selectedOffer?.status === 'active'}
        >
          <CheckCircle sx={{ mr: 1 }} />
          Activate
        </MenuItem>
        <MenuItem
          onClick={() => handleOfferAction(selectedOffer!, 'feature')}
          disabled={selectedOffer?.isFeatured}
        >
          <Star sx={{ mr: 1 }} />
          Feature
        </MenuItem>
      </Menu>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>
          Confirm Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionType} offer "{selectedOffer?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button onClick={confirmAction} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferManagement;
